import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Building2, ClipboardList, Clock, FileText, HardHat, MessagesSquare, UserRound, X, Zap } from "lucide-react";
import { api } from "@/lib/api";
import { homeLabel } from "@/lib/home";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Command, CommandInput, CommandList, CommandGroup, CommandItem } from "@/components/ui/command";
import { V } from "@/lib/vocab";

const RECENT_KEY = "askredline.recentPages";
const TONE = { red: "bg-redline-500 text-white", amber: "bg-amber-500 text-white", rose: "bg-rose-500 text-white", grey: "bg-sand-200 text-ink/70" };
const slug = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-");
const loadRecent = () => { try { return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]"); } catch { return []; } };
const pushRecent = (to) => {
  const next = [to, ...loadRecent().filter((x) => x !== to)].slice(0, 6);
  try { localStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch { /* ignore */ }
};
const fmtAgo = (iso) => {
  if (!iso) return "";
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 60) return `${Math.max(m, 0)} min ago`;
  if (m < 1440) return `${Math.round(m / 60)} h ago`;
  return `${Math.round(m / 1440)} d ago`;
};

function Row({ icon: Icon, title, sub, right, badge, tone, testid, onSelect, value }) {
  return (
    <CommandItem value={value} onSelect={onSelect} data-testid={testid} className="gap-3 rounded-xl px-3 py-2.5 cursor-pointer">
      {Icon && <Icon className="h-4 w-4 shrink-0 text-ink/50" strokeWidth={1.75} />}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm text-ink">{title}</span>
        {sub && <span className="block truncate text-xs text-ink/50">{sub}</span>}
      </span>
      {typeof badge === "number" && badge > 0 && <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${TONE[tone] || TONE.grey}`}>{badge > 99 ? "99+" : badge}</span>}
      {badge === true && <span className="h-2 w-2 rounded-full bg-red-500" />}
      {right && <span className="shrink-0 text-[11px] text-ink/45">{right}</span>}
    </CommandItem>
  );
}

// ⌘K — one box that reaches any page, record or community. Pages/actions/communities are
// filtered client-side against exactly what the sidebar shows this role; records come from
// GET /tenants/{id}/search once two characters are typed.
export function CommandPalette({ open, onOpenChange, tenantId, pages, tenants, activeTenantId, onSwitchTenant, actions }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); onOpenChange(!open); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  useEffect(() => {
    const hit = pages.find((p) => (p.end ? location.pathname === p.to : location.pathname.startsWith(p.to) && p.to !== "/dashboard"));
    if (hit) pushRecent(hit.to);
  }, [location.pathname, pages]);

  useEffect(() => { if (!open) { setQ(""); setResults(null); } }, [open]);

  useEffect(() => {
    clearTimeout(timer.current);
    const s = q.trim();
    if (!tenantId || s.length < 2) { setResults(null); setLoading(false); return; }
    setLoading(true);
    timer.current = setTimeout(() => {
      api.get(`/tenants/${tenantId}/search`, { params: { q: s } })
        .then((r) => setResults(r.data)).catch(() => setResults(null)).finally(() => setLoading(false));
    }, 220);
    return () => clearTimeout(timer.current);
  }, [q, tenantId]);

  const s = q.trim().toLowerCase();
  const match = (label) => !s || (label || "").toLowerCase().includes(s);
  const go = (to) => { onOpenChange(false); if (to) { pushRecent(to.split("?")[0]); navigate(to); } };
  const run = (a) => { onOpenChange(false); if (a.run) a.run(); else if (a.to) navigate(a.to); };

  const recent = useMemo(() => (s ? [] : loadRecent().map((to) => pages.find((p) => p.to === to)).filter(Boolean).slice(0, 4)), [s, pages, open]); // eslint-disable-line react-hooks/exhaustive-deps
  const pageHits = pages.filter((p) => match(p.label) || match(p.group));
  const actionHits = actions.filter((a) => match(a.label));
  const tenantHits = tenants.filter((t) => t.id !== activeTenantId && match(t.name)).slice(0, s ? 8 : 5);
  const r = results || {};
  const recordCount = ["residents", "requests", "conversations", "documents", "vendors"].reduce((n, k) => n + (r[k]?.length || 0), 0);
  const nothing = s && !pageHits.length && !actionHits.length && !tenantHits.length && !loading && !recordCount;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="cmdk-dialog"
        className="p-0 gap-0 overflow-hidden sm:max-w-2xl sm:top-[12%] sm:translate-y-0 max-sm:top-0 max-sm:left-0 max-sm:translate-x-0 max-sm:translate-y-0 max-sm:w-screen max-sm:max-w-none max-sm:h-dvh max-sm:max-h-dvh max-sm:rounded-none [&>button]:hidden">
        <DialogTitle className="sr-only">Search or jump to</DialogTitle>
        <Command shouldFilter={false} loop className="bg-white">
          {/* Phones have no Esc key and this sheet is full-screen — a visible way out is mandatory. */}
          <div className="flex items-center gap-2 border-b border-sand-100 pl-3 pr-2 sm:pr-3">
            <div className="min-w-0 flex-1">
              <CommandInput data-testid="cmdk-input" value={q} onValueChange={setQ} autoFocus
                placeholder={tenantId ? `Search ${V.lcs("person")}, requests, documents… or jump to a page` : `Jump to a page or ${V.lc("org")}…`} className="h-12 text-base sm:text-sm border-0" wrapperClassName="border-0 px-0" />
            </div>
            <button type="button" data-testid="cmdk-close" onClick={() => onOpenChange(false)} aria-label="Close search"
              className="shrink-0 inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-sm font-semibold text-ink/70 hover:bg-sand-100 hover:text-ink sm:px-2">
              <span className="sm:hidden">Cancel</span><X className="hidden sm:block h-4 w-4" />
            </button>
          </div>
          <CommandList className="max-h-[70dvh] sm:max-h-[440px] p-2">
            {nothing && <div data-testid="cmdk-empty" className="py-10 text-center text-sm text-ink/50">No matches for “{q.trim()}”</div>}
            {recent.length > 0 && (
              <CommandGroup heading="Recent">
                {recent.map((p) => <Row key={`r-${p.to}`} value={`recent ${p.to}`} icon={Clock} title={p.label} right={p.group} badge={p.badge} tone={p.tone} testid={`cmdk-recent-${slug(p.label)}`} onSelect={() => go(p.to)} />)}
              </CommandGroup>
            )}
            {pageHits.length > 0 && (
              <CommandGroup heading="Pages">
                {pageHits.map((p) => <Row key={p.to} value={`page ${p.to}`} icon={p.icon} title={p.label} right={p.group} badge={p.badge} tone={p.tone} testid={`cmdk-page-${slug(p.label)}`} onSelect={() => go(p.to)} />)}
              </CommandGroup>
            )}
            {r.residents?.length > 0 && (
              <CommandGroup heading={`${V.many("person")}`}>
                {r.residents.map((x) => <Row key={x.id} value={`res ${x.id}`} icon={UserRound} title={`${x.name}${homeLabel(x) ? ` · ${homeLabel(x)}` : ""}`}
                  sub={[x.phone, x.relationship, x.pending ? "awaiting approval" : ""].filter(Boolean).join(" · ")} right="Open profile" testid={`cmdk-resident-${x.id}`} onSelect={() => go(x.to)} />)}
              </CommandGroup>
            )}
            {r.requests?.length > 0 && (
              <CommandGroup heading="Requests">
                {r.requests.map((x) => <Row key={x.id} value={`req ${x.id}`} icon={ClipboardList} title={`${x.ref ? `${x.ref} · ` : ""}${x.subject}`}
                  sub={[x.priority, x.status, homeLabel(x), x.resident].filter(Boolean).join(" · ")} right="Open request"
                  badge={["urgent", "high"].includes(x.priority) && x.status !== "resolved" ? 1 : undefined} tone="red" testid={`cmdk-request-${x.id}`} onSelect={() => go(x.to)} />)}
              </CommandGroup>
            )}
            {r.conversations?.length > 0 && (
              <CommandGroup heading="Conversations">
                {r.conversations.map((x) => <Row key={x.id} value={`conv ${x.id}`} icon={MessagesSquare} title={[x.name, x.phone].filter(Boolean).join(" · ") || "Conversation"}
                  sub={[x.status, fmtAgo(x.at)].filter(Boolean).join(" · ")} right="Open thread" testid={`cmdk-conversation-${x.id}`} onSelect={() => go(x.to)} />)}
              </CommandGroup>
            )}
            {r.documents?.length > 0 && (
              <CommandGroup heading="Documents">
                {r.documents.map((x) => <Row key={x.id} value={`doc ${x.id}`} icon={FileText} title={x.title} sub={[x.kind, x.audience].filter(Boolean).join(" · ")} right="Open document" testid={`cmdk-document-${x.id}`} onSelect={() => go(x.to)} />)}
              </CommandGroup>
            )}
            {r.vendors?.length > 0 && (
              <CommandGroup heading="Vendors">
                {r.vendors.map((x) => <Row key={x.id} value={`ven ${x.id}`} icon={HardHat} title={x.name} sub={(x.trades || []).map((t) => t.replace(/_/g, " ")).join(", ")} right="Open vendor" testid={`cmdk-vendor-${x.id}`} onSelect={() => go(x.to)} />)}
              </CommandGroup>
            )}
            {loading && <div data-testid="cmdk-loading" className="px-3 py-2 text-xs text-ink/40">Searching…</div>}
            {actionHits.length > 0 && (
              <CommandGroup heading="Quick actions">
                {actionHits.map((a) => <Row key={a.id} value={`act ${a.id}`} icon={a.icon || Zap} title={a.label} testid={`cmdk-action-${a.id}`} onSelect={() => run(a)} />)}
              </CommandGroup>
            )}
            {tenantHits.length > 0 && (
              <CommandGroup heading={`Switch ${V.lc("org")}`}>
                {tenantHits.map((t) => <Row key={t.id} value={`ten ${t.id}`} icon={Building2} title={t.name} testid={`cmdk-tenant-${t.id}`} onSelect={() => { onOpenChange(false); onSwitchTenant(t.id); }} />)}
              </CommandGroup>
            )}
          </CommandList>
          <div className="flex items-center gap-4 border-t border-sand-100 px-4 py-2 text-[11px] text-ink/50">
            <span className="hidden sm:inline"><kbd className="rounded border border-sand-200 bg-sand-50 px-1">↑↓</kbd> move</span>
            <span className="hidden sm:inline"><kbd className="rounded border border-sand-200 bg-sand-50 px-1">⏎</kbd> open</span>
            <span className="hidden sm:inline"><kbd className="rounded border border-sand-200 bg-sand-50 px-1">esc</kbd> close</span>
            <span className="sm:hidden">{tenantId ? `Finds ${V.lcs("person")}, requests, conversations, documents and pages in this ${V.lc("org")}` : `Jump to a page or ${V.lc("org")}`}</span>
            <span className="ml-auto hidden sm:inline">{tenantId ? `Scoped to this ${V.lc("org")} · only what your role can see` : "Only what your role can see"}</span>
          </div>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
